import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore.ts'
import { type Component, provide } from 'vue'
import IntroductionContent from '@/features/interface/demande/af/steps/introduction/IntroductionStep.vue'
import EmployeurContent from '@/features/interface/demande/af/steps/employeur/EmployeurStep.vue'
import FormationContent from '@/features/interface/demande/af/steps/formation/FormationStep.vue'
import SalarieContent from '@/features/interface/demande/af/steps/salarie/SalarieStep.vue'
import SyntheseContent from '@/features/interface/demande/af/steps/synthese/SyntheseStep.vue'
import DocumentsContent from '@/features/interface/demande/af/steps/documents/DocumentsStep.vue'
import FinancementContent from '@/features/interface/demande/af/steps/financement/FinancementStep.vue'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import type { ActionFormationDTO, LovAfDTO } from '@/features/api/extranet/dto/Demande/AF'
import type { SetupDemande } from '@/features/interface/demande/shared/components/demande-content/DemandeContent.types.ts'
import { storeToRefs } from 'pinia'
import { useDemandeAFActions } from '@/features/interface/demande/af/useDemandeAFActions.ts'
import { AF_ACTIONS } from '@/features/interface/demande/af/useDemandeAFActions.ts'
import { AF_SALARIES_ACTIONS, useSalarieActions } from './steps/salarie/useSalarieActions'
import { useModuleActions, AF_MODULES_ACTIONS } from './steps/formation/useModuleActions'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { type DemandeStore } from '@/features/interface/demande/shared'

type TabKey = 'Introduction' | 'Employeur' | 'Salariés' | 'Formation' | 'Financement' | 'Synthèse' | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  Introduction: IntroductionContent,
  Employeur: EmployeurContent,
  Formation: FormationContent,
  Financement: FinancementContent,
  Salariés: SalarieContent,
  Synthèse: SyntheseContent,
  Documents: DocumentsContent,
} as const

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<ActionFormationDTO> = {
  Introduction: { include: (state) => [state.validationNotice] },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Formation: { includeDeep: (state) => [state.formation], include: (state) => [state.referenceProjet] },
  Salariés: {
    includeDeep: (state) => [state.salaries],
    include: (state) => [state.attestationSalariesSaisies],
  },
  Financement: {
    includeDeep: (state) => [state.financement],
  },
  Synthèse: { include: (state) => [state.commentaire, state.acceptationCg, state.attestationSurHonneur, state.attestationCadreInterventionFse, state.attestationEngagementsFse, state.attestationInformationParticipantsFse] },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
}

function customizeNatureFichiers(store: DemandeStore<'MODEL_AF', LovAfDTO>) {
  const { model, lov } = storeToRefs(store)

  if (model.value?.sousDispositif !== 'Fse') return lov.value.NatureFichier
  return lov.value.NatureFichier.map((item) => {
    if (item.code !== 'Programme') return item
    return {
      code: 'Programme',
      intitule: 'Programme (si séparé de projet de convention ou devis)',
    }
  })
}

export const SetupDemandeAF: SetupDemande<'MODEL_AF', TabKey> = {
  useStore: useDemandeAFStore,
  injectActions: () => {
    const actions = useDemandeAFActions()
    provide(AF_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Introduction',
  tabsVue: TABS_VUES,
  tabsErrorConfig: (store) => {
    const { typeContextCourant, estTT } = storeToRefs(store)
    if (estTT.value && typeContextCourant.value === TypesContextesAutorisesEnum.Adherent)
      return TABS_ERR_CONFIG
    const { Financement, ...newTabs } = TABS_ERR_CONFIG
    return newTabs
  },
  customizeNatureFichiers,
  onInitialize: () => {
    provide(AF_SALARIES_ACTIONS, useSalarieActions())
    provide(AF_MODULES_ACTIONS, useModuleActions())
  },
} as const
